import { downloadCanvas } from '@shakkerai/pro-utils';
import domtoimage from './dom-to-image';
import { drawTooltip, getCanvas, getCanvasRectColor, loadImage, rbgaObjToHex, renderColorInfo } from './helper';
import type { IProps, IRect } from './interface.ts';
import { translate } from '@shakkerai/util-i18n';

export * from './interface.ts';

let escToolTipTimer: any = null;
/**
 * 网页拾色器【吸管工具】
 */
class ColorPipette {
  container: any = {};
  listener: Record<string, (e: any) => void> = {};
  rect: IRect = { x: 0, y: 0, width: 0, height: 0 };
  canvas: any = {};
  ctx: any;
  scale = 1;
  magnifier: any = null;
  colorContainer: any = null;
  colors: string[][] = [];
  tooltipVisible = true;
  useMagnifier = false;
  background?: string | HTMLImageElement;
  afterDestroy?: () => void;
  constructor(props: IProps) {
    try {
      const { container, listener, scale = 1, useMagnifier = false, background, afterDestroy } = props;
      this.container = container || document.body;
      this.listener = listener || {};
      this.rect = this.container.getBoundingClientRect();
      this.scale = scale > 4 ? 4 : scale;
      this.useMagnifier = useMagnifier;
      this.background = background;
      this.afterDestroy = afterDestroy;
      // 去除noscript标签，可能会导致
      const noscript = document.body.querySelector('noscript');
      noscript?.parentNode?.removeChild(noscript);
      this.initCanvas();
    } catch (err) {
      console.error(err);
      this.destroy();
    }
  }
  /**
   * 初始化canvas
   */
  initCanvas() {
    const { rect, scale } = this;
    const { x, y, width, height } = rect;
    // 这里可以这种画布的层级
    const { canvas, ctx } = getCanvas({
      width: rect.width,
      height: rect.height,
      scale,
      attrs: {
        class: 'color-pipette-canvas-container',
        style: `
          position: fixed;
          
          width: ${width}px;
          height: ${height}px;
        `,
      },
    });
    // 画背景
    if (this.background) {
      if (typeof this.background === 'string') {
        ctx.fillStyle = this.background;
        ctx.fillRect(0, 0, width, height);
      } else if (this.background instanceof HTMLImageElement) {
        // 计算横向和纵向需要重复绘制的次数
        const imgWidth = this.background.width;
        const imgHeight = this.background.height;
        const repeatX = Math.ceil(width / imgWidth);
        const repeatY = Math.ceil(height / imgHeight);

        for (let y = 0; y < repeatY; y++) {
          for (let x = 0; x < repeatX; x++) {
            ctx.drawImage(this.background, x * imgWidth, y * imgHeight);
          }
        }
      }
    }
    this.canvas = canvas;
    this.ctx = ctx;
  }
  /**
   * 开始
   */
  async start() {
    try {
      await this.drawCanvas();
      // document.body.appendChild(this.canvas);
      this.container.parentElement?.appendChild(this.canvas);
      if (!escToolTipTimer) {
        const tooltip = drawTooltip(translate('press_esc_exit'));
        document.body.appendChild(tooltip);
        escToolTipTimer = setTimeout(() => {
          tooltip?.parentNode?.removeChild(tooltip);
          escToolTipTimer = null;
        }, 2000);
      }
      // 添加监听
      this.canvas.addEventListener('mousemove', this.handleMove);
      // this.canvas.addEventListener('mousedown', this.handleDown);
      this.canvas.addEventListener('click', this.handleDown);
      this.canvas.addEventListener('mouseenter', this.handleMouseenter);
      this.canvas.addEventListener('mouseleave', this.handleMouseleave);
      // this.canvas.addEventListener('wheel', this.handleWheel);
      this.canvas.addEventListener('wheel', this.handlePrevent);
      this.canvas.addEventListener('mouseup', this.handlePrevent);
      this.canvas.addEventListener('mousedown', this.handlePrevent);
      document.addEventListener('keydown', this.handleKeyDown);
    } catch (err) {
      console.error(err);
      this.destroy();
    }
  }
  /**
   * 结束销毁dom，清除事件监听
   */
  destroy() {
    this.canvas.removeEventListener('mousemove', this.handleMove);
    // this.canvas.removeEventListener('mousedown', this.handleDown);
    this.canvas.removeEventListener('click', this.handleDown);
    this.canvas.removeEventListener('mouseenter', this.handleMouseenter);
    this.canvas.removeEventListener('mouseleave', this.handleMouseleave);
    // this.canvas.removeEventListener('wheel', this.handleWheel);
    this.canvas.removeEventListener('wheel', this.handlePrevent);
    this.canvas.removeEventListener('mouseup', this.handlePrevent);
    this.canvas.removeEventListener('mousedown', this.handlePrevent);
    document.removeEventListener('keydown', this.handleKeyDown);

    this.canvas?.parentNode?.removeChild(this.canvas);
    this.colorContainer?.parentNode?.removeChild(this.colorContainer);
    if (this.afterDestroy) {
      this.afterDestroy();
    }
  }

  /**
   * 将dom节点画到canvas里
   */
  async drawCanvas() {
    try {
      const base64 = await domtoimage.toPng(this.container, { scale: this.scale });
      if (!base64) {
        console.log('xxx base64 not');
        return;
      }
      const img = await loadImage(base64);
      if (!img) {
        console.log('xxx img not');
        return;
      }
      this.ctx.drawImage(img, 0, 0, this.rect.width, this.rect.height);
      // downloadCanvas(this.canvas)
    } catch (e) {
      console.error('xxx error', e);
    }
  }

  /**
   * 处理鼠标移动
   */
  handleMove = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    const { color, colors } = this.getPointColors(e, 7);
    const { onChange = () => '' } = this.listener;
    const point = { x: e.pageX + 15, y: e.pageY + 15 };
    const colorContainer = renderColorInfo({
      containerDom: this.colorContainer,
      color,
      colors,
      point,
    });
    if (!this.colorContainer) {
      this.colorContainer = colorContainer;
      document.body.appendChild(colorContainer);
    }
    onChange({ color, colors });
  };

  /**
   * 处理鼠标按下
   */
  handleDown = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    const { onOk = () => '' } = this.listener;
    const res = this.getPointColors(e, 7);
    // console.log(JSON.stringify(res.colors, null, 4));
    onOk(res);
    this.destroy();
  };

  /**
   * 处理键盘按下Esc退出拾色
   */
  handleKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Escape') {
      this.destroy();
    }
  };

  handleMouseenter = (e: any) => {
    this.colorContainer.style.display = 'block';
  };
  handleMouseleave = (e: any) => {
    this.colorContainer.style.display = 'none';
  };

  handlePrevent = (e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * 获取鼠标点周围的颜色整列
   */
  getPointColors(e: any, areaSize?: number) {
    const { ctx, rect, scale } = this;
    let { pageX: x, pageY: y } = e;
    x -= rect.x;
    y -= rect.y;
    const color = this.getPointColor(x, y);
    const size = areaSize || 19;
    const half = Math.floor(size / 2);
    const info = { x: x - half, y: y - half, width: size, height: size };
    const colors = getCanvasRectColor(ctx, info, scale);
    return { color, colors };
  }

  /**
   * 获取鼠标点的颜色
   */
  getPointColor(x: number, y: number) {
    const { scale } = this;
    const { data } = this.ctx.getImageData(x * scale, y * scale, 1, 1);
    const r = data[0];
    const g = data[1];
    const b = data[2];
    const a = data[3] / 255;
    const rgba = { r, g, b, a };
    return rbgaObjToHex(rgba);
  }
}

export default ColorPipette;
